import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

/*
  AccountContext — real backend-backed account state for Access Control & User
  Management. Reads GET /api/account and calls the owner-gated user/account
  endpoints. Owner is derived from the authenticated user's is_owner flag.
*/

export const ROLES = { OWNER: 'owner', TEAM: 'team' };

export const TAB_DEFS = [
  { key: 'overview', label: 'Overview' },
  { key: 'brief', label: 'Brief' },
  { key: 'meetings', label: 'Meetings' },
  { key: 'boq', label: 'BOQ / Estimate' },
  { key: 'timeframe', label: 'Timeframe' },
  { key: 'tasks', label: 'Tasks' },
  { key: 'drawings', label: 'Drawings' },
  { key: 'photos', label: 'Photos' },
  { key: 'orders', label: 'Orders' },
  { key: 'materials', label: 'Materials' },
  { key: 'billing', label: 'Billing' },
  { key: 'agencies', label: 'Agencies' },
  { key: 'snagList', label: 'Snag List' },
  { key: 'clientPortal', label: 'Client Portal' },
];

export const ACTION_DEFS = [
  { key: 'canEdit', label: 'Can add/edit records' },
  { key: 'canDelete', label: 'Can delete records' },
  { key: 'canExport', label: 'Can export / download' },
];

export const FEATURE_DEFS = [
  { key: 'financials', label: 'Financials — Proposals & Design Fee' },
  { key: 'manageData', label: 'Manage Databases, Templates & Settings' },
];

const _tabs = (full) => ({
  overview: true, brief: true, meetings: true, boq: true, timeframe: true, tasks: true,
  drawings: true, photos: true, orders: true, materials: true, billing: true, agencies: true,
  snagList: full, clientPortal: true,
});

export const defaultTeamPermissions = () => ({
  tabs: _tabs(false),
  actions: { canEdit: true, canDelete: false, canExport: false },
  features: { financials: false, manageData: false },
});

export const fullTeamPermissions = () => ({
  tabs: _tabs(true),
  actions: { canEdit: true, canDelete: true, canExport: true },
  features: { financials: true, manageData: true },
});

// Backwards-compat alias
export const defaultStaffPermissions = defaultTeamPermissions;

const AccountContext = createContext(null);

export function AccountProvider({ children }) {
  const { user } = useAuth();
  const [account, setAccount] = useState({ accountName: '', ownerId: '', subscriptionPlan: '', users: [] });
  const [loading, setLoading] = useState(true);

  const isOwner = !!user?.is_owner;
  const isAdmin = isOwner; // legacy alias
  const currentUserId = user?.id || null;

  const refresh = useCallback(async () => {
    if (!isOwner) { setLoading(false); return; }
    try {
      const res = await api.get('/account');
      setAccount(res.data);
    } catch {
      /* non-owners or errors leave defaults */
    } finally {
      setLoading(false);
    }
  }, [isOwner]);

  useEffect(() => { refresh(); }, [refresh]);

  // POST /users/invite — returns { user, temp_password }
  const inviteUser = async ({ name, email, role, permissions }) => {
    const res = await api.post('/users/invite', { name, email, role, permissions });
    await refresh();
    return res.data;
  };
  // PUT /users/:id/permissions
  const updateUserPermissions = async (userId, permissions) => {
    await api.put(`/users/${userId}/permissions`, { permissions });
    await refresh();
  };
  // PUT /users/:id/role
  const updateUserRole = async (userId, role) => {
    await api.put(`/users/${userId}/role`, { role });
    await refresh();
  };
  // DELETE /users/:id
  const removeUser = async (userId) => {
    await api.delete(`/users/${userId}`);
    await refresh();
  };
  // POST /users/:id/resend-invite — returns { temp_password }
  const resendInvite = async (userId) => {
    const res = await api.post(`/users/${userId}/resend-invite`);
    return res.data;
  };
  // PUT /account/owner-profile
  const updateOwnerProfile = async ({ name, email }) => {
    await api.put('/account/owner-profile', { name, email });
    await refresh();
  };
  // PUT /account/transfer-ownership
  const transferOwnership = async (newOwnerId) => {
    await api.put('/account/transfer-ownership', { new_owner_id: newOwnerId });
    await refresh();
  };

  return (
    <AccountContext.Provider value={{
      account, loading, isOwner, isAdmin, currentUserId, refresh,
      inviteUser, updateUserPermissions, updateUserRole, removeUser, resendInvite,
      updateOwnerProfile, transferOwnership,
    }}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error('useAccount must be used within an AccountProvider');
  return ctx;
}
